import { Link } from "react-router-dom";
import { useBrand } from "../../context/SiteSettingsContext";
import BrandLogo from "../shared/BrandLogo";
import { SITE_SETTINGS } from "../../data/site";
import { useServices } from "../../context/ServicesContext";
import { generateWhatsAppUrl } from "../../lib/whatsapp";
import { Mail, Phone } from "lucide-react";

export default function Footer() {
  const BRAND = useBrand();
  const { services } = useServices();
  return (
    <footer className="relative bg-ink border-t border-charcoal" data-testid="site-footer">
      {/* Massive brand type */}
      <div className="container-emilio pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Link
              to="/"
              className="inline-flex items-center"
              aria-label={`${BRAND.name} — Home`}
            >
              <BrandLogo className="h-12" size="lg" />
            </Link>
            <p className="mt-6 text-mutedtext text-sm max-w-md leading-relaxed">
              Professional residential and commercial cleaning services throughout Singapore.
              Trusted for post-renovation, end-of-tenancy, deep floor, upholstery and specialised work.
            </p>

            <a
              href={generateWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-emerald text-ink px-5 py-3 text-sm font-semibold hover:bg-emerald-light transition-colors"
            >
              Book via WhatsApp
            </a>

            <div className="mt-8 flex flex-col gap-2 text-sm text-mutedtext">
              <a href={`tel:${BRAND.phoneRaw}`} className="inline-flex items-center gap-2 hover:text-white">
                <Phone className="w-4 h-4" /> {BRAND.phone}
              </a>
              <a href={`mailto:${BRAND.email}`} className="inline-flex items-center gap-2 hover:text-white">
                <Mail className="w-4 h-4" /> {BRAND.email}
              </a>
              <div className="mt-3 text-xs text-white/50">UEN: {BRAND.uen}</div>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-8">
            <FooterCol title="Services">
              {services.slice(0, 6).map((s) => (
                <FooterLink key={s.slug} to={s.route}>
                  {s.name}
                </FooterLink>
              ))}
              <FooterLink to="/services">All Services →</FooterLink>
            </FooterCol>
            <FooterCol title="Company">
              {SITE_SETTINGS.footerLinks.company.map((l) => (
                <FooterLink key={l.to} to={l.to}>
                  {l.label}
                </FooterLink>
              ))}
            </FooterCol>
            <FooterCol title="Legal">
              {SITE_SETTINGS.footerLinks.legal.map((l) => (
                <FooterLink key={l.to} to={l.to}>
                  {l.label}
                </FooterLink>
              ))}
            </FooterCol>
          </div>
        </div>
      </div>

      {/* Massive typography lockup */}
      <div className="container-emilio">
        <div className="border-t border-charcoal/70 py-10">
          <div className="font-display font-extrabold text-white/[0.07] select-none leading-[0.82] tracking-[-0.04em] text-[clamp(2.75rem,11.5vw,11rem)]">
            <span className="block">EMILIO'S</span>
            <span className="block">SOLUTIONS</span>
          </div>
        </div>
      </div>

      <div className="border-t border-charcoal/50">
        <div className="container-emilio py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-mutedtext">
          <p>© {new Date().getFullYear()} Emilio's Solutions. All rights reserved.</p>
          <p className="uppercase tracking-[0.2em]">Professional Cleaning — Singapore</p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <div className="text-eyebrow font-display font-bold uppercase tracking-[0.22em] text-emerald mb-4">
        {title}
      </div>
      <div className="flex flex-col gap-2.5">{children}</div>
    </div>
  );
}

function FooterLink({ to, children }) {
  return (
    <Link to={to} className="text-sm text-white/75 hover:text-emerald transition-colors">
      {children}
    </Link>
  );
}
